/**
 * E-Mail Service für den SkyVision Konfigurator
 * Versendet Angebote an Kunden und das Vertriebsteam
 */

export interface EmailConfig {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  fromEmail: string;
  fromName: string;
  salesTeamEmail: string;
}

export interface OfferEmailData {
  customerData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    street: string;
    postalCode: string;
    city: string;
    type: 'private' | 'business';
    companyName?: string;
  };
  productConfig: {
    product: string;
    dimensions: any;
    glassType: string;
    accessories: string[];
    totalPrice: number;
  };
  services: {
    installation: boolean;
    measurement: boolean;
    technicalDrawing: boolean;
  };
  offerNumber: string;
  pdfBase64?: string;
}

/**
 * WordPress AJAX-basierter E-Mail-Versand
 * Nutzt die WordPress wp_mail() Funktion
 */
export class EmailService {
  private ajaxUrl: string;
  private nonce: string;

  constructor() {
    // WordPress AJAX URL und Nonce aus globalen Variablen
    this.ajaxUrl = (window as any).skyvision_ajax?.ajax_url || '/wp-admin/admin-ajax.php';
    this.nonce = (window as any).skyvision_ajax?.nonce || '';
  }

  /**
   * Sendet E-Mail an Kunde mit Angebot
   */
  async sendCustomerEmail(data: OfferEmailData): Promise<boolean> {
    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: (() => {
          const formData = new URLSearchParams();
          formData.append('action', 'skyvision_send_offer_email');
          formData.append('nonce', this.nonce);
          formData.append('recipient', 'customer');
          
          // Daten einzeln anhängen statt als JSON-String
          formData.append('customerData', JSON.stringify(data.customerData));
          formData.append('productConfig', JSON.stringify(data.productConfig));
          formData.append('services', JSON.stringify(data.services));
          formData.append('offerNumber', data.offerNumber);
          formData.append('pdfBase64', data.pdfBase64);
          
          return formData;
        })()
      });

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      console.error('Fehler beim Senden der Kunden-E-Mail:', error);
      return false;
    }
  }

  /**
   * Sendet E-Mail an Vertriebsteam
   */
  async sendSalesEmail(data: OfferEmailData): Promise<boolean> {
    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: (() => {
          const formData = new URLSearchParams();
          formData.append('action', 'skyvision_send_offer_email');
          formData.append('nonce', this.nonce);
          formData.append('recipient', 'sales');
          
          // Daten einzeln anhängen statt als JSON-String
          formData.append('customerData', JSON.stringify(data.customerData));
          formData.append('productConfig', JSON.stringify(data.productConfig));
          formData.append('services', JSON.stringify(data.services));
          formData.append('offerNumber', data.offerNumber);
          formData.append('pdfBase64', data.pdfBase64);
          
          return formData;
        })()
      });

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      console.error('Fehler beim Senden der Vertriebs-E-Mail:', error);
      return false;
    }
  }

  /**
   * Speichert Angebot in der Datenbank
   */
  async saveOffer(data: OfferEmailData): Promise<boolean> {
    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: (() => {
          const formData = new URLSearchParams();
          formData.append('action', 'skyvision_save_offer');
          formData.append('nonce', this.nonce);
          
          // Daten einzeln anhängen statt als JSON-String
          formData.append('customerData', JSON.stringify(data.customerData));
          formData.append('productConfig', JSON.stringify(data.productConfig));
          formData.append('services', JSON.stringify(data.services));
          formData.append('offerNumber', data.offerNumber);
          formData.append('pdfBase64', data.pdfBase64);
          
          return formData;
        })()
      });

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      console.error('Fehler beim Speichern des Angebots:', error);
      return false;
    }
  }
}

/**
 * E-Mail Templates
 */
export const EmailTemplates = {
  customer: {
    subject: (offerNumber: string) => `Ihr SkyVision Angebot ${offerNumber}`,
    html: (data: OfferEmailData) => `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Ihr SkyVision Angebot</title>
        <style>
          body { font-family: Arial, sans-serif; color: #333; }
          .header { background-color: #77b829; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; }
          .footer { background-color: #f5f5f5; padding: 15px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Ihr SkyVision Angebot</h1>
          <p>Angebotsnummer: ${data.offerNumber}</p>
        </div>
        <div class="content">
          <h2>Sehr geehrte${data.customerData.type === 'business' ? 'r' : ''} ${data.customerData.type === 'business' ? data.customerData.companyName : data.customerData.firstName + ' ' + data.customerData.lastName},</h2>
          
          <p>vielen Dank für Ihr Interesse an unseren SkyVision Flachdachfenstern.</p>
          
          <h3>Ihre Konfiguration:</h3>
          <ul>
            <li><strong>Produkt:</strong> ${data.productConfig.product}</li>
            <li><strong>Verglasung:</strong> ${data.productConfig.glassType === 'double' ? '2-fach' : '3-fach'} Isolierverglasung</li>
            <li><strong>Gesamtpreis:</strong> ${data.productConfig.totalPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</li>
          </ul>
          
          <p>Das detaillierte Angebot finden Sie im Anhang als PDF.</p>
          
          <p>Bei Fragen stehen wir Ihnen gerne zur Verfügung.</p>
          
          <p>Mit freundlichen Grüßen<br>Ihr SkyVision Team</p>
        </div>
        <div class="footer">
          <p>Diese E-Mail wurde automatisch generiert.</p>
        </div>
      </body>
      </html>
    `
  },
  
  sales: {
    subject: (offerNumber: string) => `Neues Angebot erstellt: ${offerNumber}`,
    html: (data: OfferEmailData) => `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Neues Angebot</title>
        <style>
          body { font-family: Arial, sans-serif; color: #333; }
          .header { background-color: #475569; color: white; padding: 20px; }
          .content { padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Neues Angebot erstellt</h1>
          <p>Angebotsnummer: ${data.offerNumber}</p>
        </div>
        <div class="content">
          <h2>Kundendaten:</h2>
          <table>
            <tr><th>Name</th><td>${data.customerData.firstName} ${data.customerData.lastName}</td></tr>
            <tr><th>E-Mail</th><td>${data.customerData.email}</td></tr>
            <tr><th>Telefon</th><td>${data.customerData.phone}</td></tr>
            <tr><th>Adresse</th><td>${data.customerData.street}, ${data.customerData.postalCode} ${data.customerData.city}</td></tr>
            <tr><th>Kundentyp</th><td>${data.customerData.type === 'business' ? 'Gewerblich' : 'Privat'}</td></tr>
            ${data.customerData.companyName ? `<tr><th>Firma</th><td>${data.customerData.companyName}</td></tr>` : ''}
          </table>
          
          <h2>Produktkonfiguration:</h2>
          <table>
            <tr><th>Produkt</th><td>${data.productConfig.product}</td></tr>
            <tr><th>Verglasung</th><td>${data.productConfig.glassType === 'double' ? '2-fach' : '3-fach'} Isolierverglasung</td></tr>
            <tr><th>Gesamtpreis</th><td>${data.productConfig.totalPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td></tr>
          </table>
          
          <h2>Services:</h2>
          <ul>
            ${data.services.installation ? '<li>✅ Montage durch GlasoLux</li>' : '<li>❌ Montage durch GlasoLux</li>'}
            ${data.services.measurement ? '<li>✅ Aufmaß durch GlasoLux</li>' : '<li>❌ Aufmaß durch GlasoLux</li>'}
            ${data.services.technicalDrawing ? '<li>✅ Technische Zeichnung</li>' : '<li>❌ Technische Zeichnung</li>'}
          </ul>
        </div>
      </body>
      </html>
    `
  }
};
